"use client"

import type { ReactNode } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Lock } from "lucide-react"
import type { WebWidgetConfig } from "@/lib/agents/messaging/types"
import { WIDGET_DEFAULT_CONFIG, WIDGET_FONTS } from "@/lib/agents/messaging/channels/web-widget/constants"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




interface WidgetAppearanceFieldsProps {
  config: WebWidgetConfig
  onChange: (config: WebWidgetConfig) => void
  canManage: boolean
  avatarControl?: ReactNode
  canCustomizeBranding?: boolean
  showBrandingToggle?: boolean
}

export function WidgetAppearanceFields({
  config,
  onChange,
  canManage,
  avatarControl,
  canCustomizeBranding = true,
  showBrandingToggle = false,
}: WidgetAppearanceFieldsProps) {

  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWidgetAppearanceFields")

  const patch = (next: Partial<WebWidgetConfig>) => onChange({ ...config, ...next })
  const brandingEditable = canManage && canCustomizeBranding

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("header")}>{tUi("header")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label><span key={tUi("avatar")}>{tUi("avatar")}</span></Label>
            {avatarControl ?? (
              <Input
                value={config.avatarUrl ?? ""}
                onChange={(e) => patch({ avatarUrl: e.target.value })}
                placeholder={"https://example.com/avatar.png"}
                disabled={!canManage}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="widget-title"><span key={tUi("headerTitle")}>{tUi("headerTitle")}</span></Label>
              <Input
                id="widget-title"
                value={config.title ?? ""}
                onChange={(e) => patch({ title: e.target.value })}
                placeholder={tUi("eGChatWithUs")}
                disabled={!canManage}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="widget-subtitle"><span key={tUi("headerSubtitle")}>{tUi("headerSubtitle")}</span></Label>
              <Input
                id="widget-subtitle"
                value={config.headerSubtitle ?? ""}
                onChange={(e) => patch({ headerSubtitle: e.target.value })}
                placeholder={tUi("eGUsuallyRepliesInMinutes")}
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label><span key={tUi("headerStyle")}>{tUi("headerStyle")}</span></Label>
            <div className="flex gap-2">
              {(["solid", "gradient"] as const).map((style) => (
                <Button
                  key={style}
                  variant={config.headerStyle === style ? "default" : "outline"}
                  size="sm"
                  onClick={() => patch({ headerStyle: style })}
                  disabled={!canManage}
                >
                  <span key={style === "solid" ? tUi("solid") : tUi("gradient")}>{style === "solid" ? tUi("solid") : tUi("gradient")}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("chatWindow")}>{tUi("chatWindow")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="widget-welcome"><span key={tUi("welcomeMessage")}>{tUi("welcomeMessage")}</span></Label>
            <Input
              id="widget-welcome"
              value={config.welcomeMessage ?? ""}
              onChange={(e) => patch({ welcomeMessage: e.target.value })}
              placeholder={tUi("eGHiHowCanWeHelpYou")}
              disabled={!canManage}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label><span key={tUi("font")}>{tUi("font")}</span></Label>
              <Select
                value={config.fontFamily ?? "System"}
                onValueChange={(value) => patch({ fontFamily: value })}
                disabled={!canManage}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WIDGET_FONTS.map((font) => (
                    <SelectItem key={font.value} value={font.value}>
                      <span style={{ fontFamily: font.css }}>{font.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label><span><span key={tUi("fontSize")}>{tUi("fontSize")}</span></span> <span className="text-gray-400 font-normal"><TranslationText>{tUi.rich("value0Px", { value0: String(config.fontSize ?? 14) })}</TranslationText></span></Label>
              <Slider
                value={[config.fontSize ?? 14]}
                onValueChange={([value]) => patch({ fontSize: value })}
                min={12}
                max={18}
                step={1}
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="widget-color"><span key={tUi("primaryColor")}>{tUi("primaryColor")}</span></Label>
              <div className="flex gap-2">
                <Input
                  id="widget-color"
                  type="color"
                  value={config.primaryColor ?? WIDGET_DEFAULT_CONFIG.primaryColor}
                  onChange={(e) => patch({ primaryColor: e.target.value })}
                  className="w-12 h-9 p-1 cursor-pointer"
                  disabled={!canManage}
                />
                <Input
                  value={config.primaryColor ?? WIDGET_DEFAULT_CONFIG.primaryColor}
                  onChange={(e) => patch({ primaryColor: e.target.value })}
                  className="flex-1 font-mono text-sm"
                  disabled={!canManage}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label><span><span key={tUi("borderRadius")}>{tUi("borderRadius")}</span></span> <span className="text-gray-400 font-normal"><TranslationText>{tUi.rich("value0Px", { value0: String(config.borderRadius ?? 12) })}</TranslationText></span></Label>
              <Slider
                value={[config.borderRadius ?? 12]}
                onValueChange={([value]) => patch({ borderRadius: value })}
                min={0}
                max={40}
                step={2}
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label><span><span key={tUi("typingIndicatorDelay")}>{tUi("typingIndicatorDelay")}</span></span> <span className="text-gray-400 font-normal">{config.typingIndicatorDelay ?? 4}s</span></Label>
            <Slider
              value={[config.typingIndicatorDelay ?? 4]}
              onValueChange={([value]) => patch({ typingIndicatorDelay: value })}
              min={0}
              max={10}
              step={1}
              disabled={!canManage}
            />
            <p className="text-xs text-gray-500"><span key={tUi("delayBeforeTypingDotsAppearAfterVisitorSends")}>{tUi("delayBeforeTypingDotsAppearAfterVisitorSends")}</span></p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("branding")}>{tUi("branding")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {showBrandingToggle && (
            <div className="flex items-center justify-between">
              <div>
                <Label><span key={tUi("showFooterBranding")}>{tUi("showFooterBranding")}</span></Label>
                <p className="text-xs text-gray-500 mt-0.5"><span key={tUi("keepTheWidgetFooterVisible")}>{tUi("keepTheWidgetFooterVisible")}</span></p>
              </div>
              <Switch
                checked={config.showBranding !== false}
                onCheckedChange={(checked) => patch({ showBranding: checked })}
                disabled={!canManage}
              />
            </div>
          )}

          {(!showBrandingToggle || config.showBranding !== false) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="widget-branding-text"><span key={tUi("footerText")}>{tUi("footerText")}</span></Label>
                {!canCustomizeBranding && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p><span key={tUi("upgradeToAPaidPlanToCustomizeBranding")}>{tUi("upgradeToAPaidPlanToCustomizeBranding")}</span></p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <Input
                id="widget-branding-text"
                value={config.brandingText ?? WIDGET_DEFAULT_CONFIG.brandingText}
                onChange={(e) => patch({ brandingText: e.target.value })}
                placeholder={WIDGET_DEFAULT_CONFIG.brandingText}
                maxLength={100}
                disabled={!brandingEditable}
                className={!brandingEditable ? "opacity-60" : ""}
              />
              {!canCustomizeBranding && (
                <p className="text-xs text-gray-500"><span key={tUi("availableOnPaidPlans")}>{tUi("availableOnPaidPlans")}</span></p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
